import React from 'react';

import { Stack, StackChild } from '@tanium/react-stack';

import { NoOverflow } from '../utils/NoOverflow';
import { BaseLegendItem } from './BaseLegendItem';
import { LegendCircleIcon, LegendCircleIconProps } from './LegendCircleIcon';

export interface LegendItemProps {
  /**
   * Whether the label should be allowed to wrap to more than one line. If `false`, the label will
   * be rendered with styling to prevent wrapping and truncate overflow with ellipsis. Defaults to
   * `false`.
   */
  allowWrap?: boolean;
  /**
   * The color associated with the item.
   */
  color: LegendCircleIconProps['color'];
  /**
   * The label to identify the legend item.
   */
  label: React.ReactNode;
  /**
   * Handler to invoke when the legend item is clicked.
   */
  onClick?: React.MouseEventHandler;
  /**
   * Handler to invoke when the pointer moves out from the legend item.
   */
  onPointerOut?: React.MouseEventHandler;
  /**
   * Handler to invoke when the pointer moves over the legend item.
   */
  onPointerOver?: React.MouseEventHandler;
}

/**
 * A representation of a single legend item, i.e., a colored icon and a corresponding label.
 */
export const LegendItem = ({
  allowWrap = false,
  color,
  label,
  onClick,
  onPointerOut,
  onPointerOver,
}: LegendItemProps): JSX.Element => (
  <BaseLegendItem onClick={onClick} onPointerOut={onPointerOut} onPointerOver={onPointerOver} width="100%">
    <Stack width="100%" itemSpacing={1} alignItems="center">
      <StackChild grow={0} shrink={0}>
        <LegendCircleIcon color={color} />
      </StackChild>
      <StackChild component={allowWrap ? undefined : NoOverflow}>{label}</StackChild>
    </Stack>
  </BaseLegendItem>
);
